import { useContext, useMemo } from 'react';
import { CommonModalV3 as Modal, POPUP_TYPE } from '../../../subComponents/commonModal';
import { ButtonV2, VARIENT } from '../../../subComponents/button';
import { InputV2 } from '../../../subComponents/input';
import { useTime } from '../../../../hooks/useTime';
import { round } from '../../../../utils/time';
import { RecoverPasswordContext } from '../../contexts';
import {
  MODAL_KEY, CONFIRMATION_POPUP_KEY, RESPONSE_POPUP_KEY, ALLOWED_KEY_CODE,
  NUMERIC_KEY_CODE, TAB_KEY_CODE, ALPHABETICAL_KEY_CODE, FORM_STEP,
  IPasswordForm, OTP_VALIDITY_IN_MINS,
} from '../../constant';

const COMMON_INPUT_STYLE = { width: '46px' };

const INPUTS = [
  {
    name: 'digit1',
    autoFocus: true,
    style: COMMON_INPUT_STYLE,
    nextKey: 'digit2',
  },
  {
    name: 'digit2',
    autoFocus: false,
    style: COMMON_INPUT_STYLE,
    prevKey: 'digit1',
    nextKey: 'digit3',
  },
  {
    name: 'digit3',
    autoFocus: false,
    style: COMMON_INPUT_STYLE,
    prevKey: 'digit2',
    nextKey: 'digit4',
  },
  {
    name: 'digit4',
    autoFocus: false,
    style: COMMON_INPUT_STYLE,
    prevKey: 'digit3',
    nextKey: 'digit5',
  },
  {
    name: 'digit5',
    autoFocus: false,
    style: COMMON_INPUT_STYLE,
    prevKey: 'digit4',
    nextKey: 'digit6',
  },
  {
    name: 'digit6',
    autoFocus: false,
    style: COMMON_INPUT_STYLE,
    prevKey: 'digit5',
  },
];

export const ComfirmOTP = () => {
  const {
    modalCollection,

    recoverForm,

    recoverPassword,

    otp,
    setOTP,

    otpExpiryTime,

    setFormStep,

    setPasswordForm,

    handler: {
      handleMultipleModalClose,
      handleModalOpen,
      handleSendOTP,
    },

    action: {
      validateOTP = async () => ({}),
    },
  } = useContext(RecoverPasswordContext);

  const currentTime = useTime({ interval: 1 });

  const { timeRemaining, showResendOTP } = useMemo(() => {
    if (recoverPassword.sendOTP.loading) {
      return {
        timeRemaining: `Resending another 6-digit code to the above phone number.`,
        showResendOTP: true,
      };
    }

    if (otpExpiryTime.current === 0) {
      return {
        timeRemaining: 'The code will expire in 10 mins',
        showResendOTP: false,
      };
    }

    if (currentTime.epochTime >= otpExpiryTime.current) {
      return {
        timeRemaining: 'The code has expired',
        showResendOTP: true,
      };
    }

    const epochTimeRemaining = otpExpiryTime.current - currentTime.epochTime;
    const secondsRemaining = parseInt(epochTimeRemaining / 1000);
    const exactSecondsRemaining = parseInt(secondsRemaining % 60);
    const exactMinutesRemaining = parseInt(secondsRemaining / 60);
    if (exactMinutesRemaining < 1 && exactSecondsRemaining < 1) {
      return {
        timeRemaining: 'The code has expired',
        showResendOTP: true,
      };
    }
    let showResendOTP = false;
    // show resent otp button after 1 minute
    if (exactMinutesRemaining < (OTP_VALIDITY_IN_MINS - 1)) {
      showResendOTP = true;
    }

    return {
      timeRemaining: `The code will expire in ${round(exactMinutesRemaining)} min(s) ${round(exactSecondsRemaining)} sec(s)`,
      showResendOTP,
    };
  }, [otpExpiryTime, currentTime, recoverPassword.sendOTP]);

  const disabledValidate = useMemo(() => {
    if (recoverPassword.sendOTP.loading) {
      return true;
    }

    if (recoverPassword.validateOTP.loading) {
      return true;
    }

    const errorFields = new Map();

    INPUTS.forEach(({ name }) => {
      if (!otp[name]?.length) {
        errorFields.set(name, {});
      }
    });

    return errorFields.size > 0;
  }, [otp, recoverPassword]);

  const disabledResend = useMemo(() => {
    if (recoverPassword.sendOTP.loading) {
      return true;
    }

    if (recoverPassword.validateOTP.loading) {
      return true;
    }

    return !showResendOTP;
  }, [showResendOTP, recoverPassword]);

  const uniqueCode = useMemo(() => {
    let code = '';
    INPUTS.forEach(({ name }) => {
      code = `${code}${otp[name]}`;
    });
    return code;
  }, [otp]);

  const disabledBack = useMemo(() => {
    if (recoverPassword.sendOTP.loading) {
      return true;
    }

    if (recoverPassword.validateOTP.loading) {
      return true;
    }

    return false;
  }, [recoverPassword]);

  const responsePopup = useMemo(() => {
    let popup = {
      has: false,
      heading: '',
      type: POPUP_TYPE.INFO,
      message: '',
    };

    popup.has = modalCollection.has(RESPONSE_POPUP_KEY.CONFIRM_OTP);
    if (popup.has) {
      const modal = modalCollection.get(RESPONSE_POPUP_KEY.CONFIRM_OTP);
      popup = {
        ...popup,
        ...modal,
      };
    }

    return popup;
  }, [modalCollection]);

  const handleOTPPress = (event) => {
    if (!ALLOWED_KEY_CODE.has(event?.keyCode)) {
      event.preventDefault();
      return {};
    }

    if (event?.target?.value?.length >= 1 && ALPHABETICAL_KEY_CODE.has(event?.keyCode)) {
      event.preventDefault();
      return {};
    }

    if (event?.target?.value?.length >= 1 && NUMERIC_KEY_CODE.has(event?.keyCode)) {
      event.preventDefault();
      return {};
    }

    if (NUMERIC_KEY_CODE.has(event?.keyCode) && event?.shiftKey) {
      event.preventDefault();
      return {};
    }

    if (event?.target?.value?.length === 0 && TAB_KEY_CODE.has(event?.keyCode) && !event?.shiftKey) {
      event.preventDefault();
      return {};
    }

    return {};
  };

  const handleOTPChange = ({ nextKey }, event) => {
    const { target } = event;
    setOTP({ ...otp, [target.name]: target.value || '' });

    if (nextKey && target.value.length === 1) {
      const nextInput = document.querySelector(`[name="${nextKey}"]`);
      nextInput.focus();
    }
  };

  const handleValidate = async (event) => {
    const requestBody = {
      ...recoverForm,
      uniqueCode,
    };

    try {
      // eslint-disable-next-line no-unused-vars
      const { data, status } = await validateOTP(requestBody);
      if (status === 200) {
        handleMultipleModalClose({ keys: [RESPONSE_POPUP_KEY.CONFIRM_OTP, MODAL_KEY.CONFIRM_OTP] });
        setFormStep(FORM_STEP.STEP_2);
        setPasswordForm({
          ...IPasswordForm(),
          ...requestBody,
        });
      }

      if (status !== 200) {
        /*
        const {
          Message: message = 'Please try again.',
          Status: heading = 'Verification failed',
        } = data;
        */

        const message = 'Please try again.';
        const heading = 'Verification failed';

        handleModalOpen({
          key: RESPONSE_POPUP_KEY.CONFIRM_OTP,
          modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message,
          }
        });
      }
    } catch (e) {

    }
  };

  const Template = (
    <div className='flex flex-column flex-fill gap-16'>
      <div className='flex bold font-24'>
        Enter Code
      </div>

      <div className='flex flex-column font-16'>
        A 6-digit code has been sent to your phone number {recoverForm.phone}.
      </div>

      <div className='flex flex-column gap-8 justify-content-center min-height-240'>
        <div className='flex font-12 line-18 align-items-center justify-content-center'>
          Enter code
        </div>

        <div className='flex align-items-center justify-content-center gap-16'>
          {
            INPUTS.map(({ name, autoFocus, style, prevKey, nextKey }) => (
              <InputV2
                key={name}
                name={name}
                autoFocus={autoFocus}
                style={style}
                value={otp[name]}
                onKeyDown={handleOTPPress.bind(this)}
                onChange={handleOTPChange.bind(this, { prevKey, nextKey })}
              />
            ))
          }
        </div>

        <div className='flex font-12 line-18 align-items-center justify-content-center'>
          {timeRemaining}
        </div>

        {
          showResendOTP && (
            <ButtonV2
              label={(
                <span className='underline'>Resend Code</span>
              )}
              variant={VARIENT.noOutlinedButtonStyle}
              disabled={disabledResend}
              loading={recoverPassword.sendOTP.loading}
              onClick={handleSendOTP}
            />
          )
        }
      </div>
    </div>
  );

  const Actions = (
    <div className='flex flex-column justify-content-flex-end gap-8'>
      <ButtonV2
        label='Verify'
        disabled={disabledValidate}
        loading={recoverPassword.validateOTP.loading}
        onClick={handleValidate.bind(this, {})}
      />

      <ButtonV2
        label={(
          <span className='underline'>Back</span>
        )}
        variant={VARIENT.noOutlinedButtonStyle}
        disabled={disabledBack}
        onClick={handleModalOpen.bind(this, { key: CONFIRMATION_POPUP_KEY.CONFIRM_OTP })}
      />
    </div>
  );

  return (
    <Modal
      title=''
      open={modalCollection.has(MODAL_KEY.CONFIRM_OTP)} // modalCollection.has(MODAL_KEY.CONFIRM_OTP)
      style={{}}
      closeConfirmation={modalCollection.has(CONFIRMATION_POPUP_KEY.CONFIRM_OTP)}
      onCloseConfirmationYes={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.CONFIRM_OTP, CONFIRMATION_POPUP_KEY.CONFIRM_OTP] })}
      onCloseConfirmationNo={handleMultipleModalClose.bind(this, { keys: [CONFIRMATION_POPUP_KEY.CONFIRM_OTP] })}
      responsePopup={responsePopup.has}
      responsePopupType={responsePopup.type}
      responsePopupHeading={responsePopup.heading}
      responsePopupMessage={responsePopup.message}
      onResponsePopupOk={handleMultipleModalClose.bind(this, { keys: [RESPONSE_POPUP_KEY.CONFIRM_OTP] })}
    >
      <div className='flex flex-column flex-fill m-5 recover-password-container'>
        {Template}
        {Actions}
      </div>
    </Modal>
  );
};

export default ComfirmOTP;
